import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePopoverPosition } from "./hooks/usePopoverPosition.js";

const WHEEL_SIZE = 180;

export const DEFAULT_COLOR = "#855cd6";

const QUICK_COLORS = [
    "#e2413c",
    "#f2711c",
    "#f7c948",
    "#4caf50",
    "#0fbd8c",
    "#2196f3",
    "#855cd6",
    "#e05a9c",
];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const hsvToRgb = (h, s, v) => {
    const c = v * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = v - c;
    const [r, g, b] =
        h < 60
            ? [c, x, 0]
            : h < 120
              ? [x, c, 0]
              : h < 180
                ? [0, c, x]
                : h < 240
                  ? [0, x, c]
                  : h < 300
                    ? [x, 0, c]
                    : [c, 0, x];

    return [
        Math.round((r + m) * 255),
        Math.round((g + m) * 255),
        Math.round((b + m) * 255),
    ];
};

const rgbToHex = (r, g, b) =>
    `#${[r, g, b].map((n) => n.toString(16).padStart(2, "0")).join("")}`;

const hexToRgb = (hex) => {
    const normalized = /^#[0-9a-f]{6}$/i.test(hex || "") ? hex : DEFAULT_COLOR;
    return [
        parseInt(normalized.slice(1, 3), 16),
        parseInt(normalized.slice(3, 5), 16),
        parseInt(normalized.slice(5, 7), 16),
    ];
};

const rgbToHsv = (r, g, b) => {
    const rn = r / 255;
    const gn = g / 255;
    const bn = b / 255;
    const max = Math.max(rn, gn, bn);
    const min = Math.min(rn, gn, bn);
    const d = max - min;

    let h = 0;
    if (d !== 0) {
        if (max === rn) h = 60 * (((gn - bn) / d) % 6);
        else if (max === gn) h = 60 * ((bn - rn) / d + 2);
        else h = 60 * ((rn - gn) / d + 4);
    }
    if (h < 0) h += 360;

    return [h, max === 0 ? 0 : d / max, max];
};

export const isHexColor = (value) => /^#[0-9a-f]{6}$/i.test(value || "");

const ColorPicker = ({ color, onChange, label = "Color" }) => {
    const [open, setOpen] = useState(false);
    const [hue, setHue] = useState(0);
    const [saturation, setSaturation] = useState(1);
    const [value, setValue] = useState(1);

    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const popoverRef = useRef(null);
    const draggingRef = useRef(false);

    const popoverLeft = usePopoverPosition(open, containerRef, popoverRef);

    useEffect(() => {
        const [h, s, v] = rgbToHsv(...hexToRgb(color));
        setHue(h);
        setSaturation(s);
        setValue(v || 1);
    }, [color]);

    useEffect(() => {
        if (!open) return;

        const onPointerDown = (event) => {
            if (!containerRef.current?.contains(event.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onPointerDown);
        return () => document.removeEventListener("mousedown", onPointerDown);
    }, [open]);

    useEffect(() => {
        if (!open) return;

        const canvas = canvasRef.current;
        if (!canvas) return;

        const ctx = canvas.getContext("2d");
        const image = ctx.createImageData(WHEEL_SIZE, WHEEL_SIZE);
        const radius = WHEEL_SIZE / 2;

        for (let y = 0; y < WHEEL_SIZE; y++) {
            for (let x = 0; x < WHEEL_SIZE; x++) {
                const dx = x - radius + 0.5;
                const dy = y - radius + 0.5;
                const distance = Math.sqrt(dx * dx + dy * dy);
                const index = (y * WHEEL_SIZE + x) * 4;

                if (distance > radius) {
                    image.data[index + 3] = 0;
                    continue;
                }

                const angle = (Math.atan2(dy, dx) * 180) / Math.PI;
                const [r, g, b] = hsvToRgb(
                    (angle + 360) % 360,
                    distance / radius,
                    value,
                );

                image.data[index] = r;
                image.data[index + 1] = g;
                image.data[index + 2] = b;
                // Fade the outer pixel ring so the wheel edge does not look jagged
                image.data[index + 3] =
                    distance > radius - 1 ? (radius - distance) * 255 : 255;
            }
        }

        ctx.putImageData(image, 0, 0);
    }, [open, value]);

    const pickFromEvent = useCallback(
        (event) => {
            const canvas = canvasRef.current;
            if (!canvas) return;

            const rect = canvas.getBoundingClientRect();
            const radius = rect.width / 2;
            const dx = event.clientX - rect.left - radius;
            const dy = event.clientY - rect.top - radius;
            const distance = Math.sqrt(dx * dx + dy * dy);

            const nextHue = ((Math.atan2(dy, dx) * 180) / Math.PI + 360) % 360;
            const nextSaturation = clamp(distance / radius, 0, 1);

            setHue(nextHue);
            setSaturation(nextSaturation);
            onChange(rgbToHex(...hsvToRgb(nextHue, nextSaturation, value)));
        },
        [onChange, value],
    );

    const onValueChange = (event) => {
        const nextValue = Number(event.target.value) / 100;
        setValue(nextValue);
        onChange(rgbToHex(...hsvToRgb(hue, saturation, nextValue)));
    };

    const markerAngle = (hue * Math.PI) / 180;
    const markerRadius = saturation * (WHEEL_SIZE / 2);
    const markerLeft = WHEEL_SIZE / 2 + Math.cos(markerAngle) * markerRadius;
    const markerTop = WHEEL_SIZE / 2 + Math.sin(markerAngle) * markerRadius;

    return (
        <div className="color-picker" ref={containerRef}>
            <button
                type="button"
                className="color-picker-swatch"
                style={{ background: color }}
                onClick={() => setOpen(!open)}
                title={`${label}: ${color}`}
                aria-label={`${label}: ${color}`}
            />

            {open && (
                <div
                    className="color-picker-popover"
                    ref={popoverRef}
                    style={{ left: `${popoverLeft}px` }}
                >
                    <div
                        className="color-picker-wheel-wrap"
                        style={{ width: WHEEL_SIZE, height: WHEEL_SIZE }}
                    >
                        <canvas
                            ref={canvasRef}
                            className="color-picker-wheel"
                            width={WHEEL_SIZE}
                            height={WHEEL_SIZE}
                            onMouseDown={(e) => {
                                draggingRef.current = true;
                                pickFromEvent(e);
                            }}
                            onMouseMove={(e) => {
                                if (draggingRef.current) pickFromEvent(e);
                            }}
                            onMouseUp={() => {
                                draggingRef.current = false;
                            }}
                            onMouseLeave={() => {
                                draggingRef.current = false;
                            }}
                        />
                        <span
                            className="color-picker-marker"
                            style={{
                                left: `${markerLeft}px`,
                                top: `${markerTop}px`,
                                background: color,
                            }}
                        />
                    </div>

                    <input
                        type="range"
                        className="color-picker-brightness"
                        min="20"
                        max="100"
                        value={Math.round(value * 100)}
                        onChange={onValueChange}
                        style={{
                            background: `linear-gradient(to right, #000, ${rgbToHex(
                                ...hsvToRgb(hue, saturation, 1),
                            )})`,
                        }}
                        title="Brillo"
                    />

                    <div className="color-picker-quick">
                        {QUICK_COLORS.map((quick) => (
                            <button
                                key={quick}
                                type="button"
                                className={`color-picker-quick-item${
                                    quick.toLowerCase() ===
                                    (color || "").toLowerCase()
                                        ? " selected"
                                        : ""
                                }`}
                                style={{ background: quick }}
                                onClick={() => onChange(quick)}
                                title={quick}
                            />
                        ))}
                    </div>

                    <div className="color-picker-value">{color}</div>
                </div>
            )}
        </div>
    );
};

export default ColorPicker;
